import React, { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  studioUnlock, createStudioProject, getStudioProject, listStudioProjects,
  synthesizeStudio, deleteStudioProject, studioExportUrl, fetchStudioExport,
  listAnalyses, studioQueueOnboarding,
} from "@/lib/api";
import {
  Sparkle, LockSimple, ArrowRight, Plus, X, Package, CheckCircle,
  CircleNotch, Warning, Copy, DownloadSimple, Star, ClockCounterClockwise,
  Spinner, Path,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const UNLOCK_KEY = "iqstudio_unlocked";
const CODE_KEY = "iqstudio_code";
const PRICE = 39;

function getCode() {
  return localStorage.getItem(CODE_KEY) || "";
}

/* ---------------- Lock gate ---------------- */

function LockGate({ onUnlock }) {
  const [code, setCode] = useState("");
  const m = useMutation({
    mutationFn: () => studioUnlock(code.trim()),
    onSuccess: () => {
      localStorage.setItem(UNLOCK_KEY, "1");
      localStorage.setItem(CODE_KEY, code.trim());
      toast.success("IQ Studio unlocked");
      onUnlock();
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Invalid access code"),
  });

  return (
    <div className="max-w-3xl mx-auto px-6 lg:px-12 py-20" data-testid="studio-lock-gate">
      <div className="font-mono text-[11px] uppercase tracking-widest text-emerald-700 mb-6 flex items-center gap-2">
        <Sparkle size={14} weight="fill" /> IQ Studio · Pro
      </div>
      <h1 className="font-display text-5xl sm:text-6xl font-bold tracking-tighter text-zinc-950 leading-[0.95]">
        Fuse three products<br />into one super-product.
      </h1>
      <p className="mt-6 max-w-2xl text-zinc-600 text-base leading-relaxed">
        Stack up to three SaaS teardowns, and IQ Studio synthesises a single combined
        product — a sharp feature set, a build roadmap, and a ready-to-paste
        <span className="font-semibold text-zinc-900"> Emergent plan</span>,
        <span className="font-semibold text-zinc-900"> Claude Code plan</span> and
        <span className="font-semibold text-zinc-900"> PRD</span>.
      </p>

      <div className="mt-10 border border-zinc-950 bg-white">
        <div className="flex items-center justify-between px-6 py-5 border-b border-zinc-200">
          <div>
            <div className="font-display text-2xl font-bold text-zinc-950">IQ Studio pass</div>
            <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mt-1">
              One-time · super-product builds
            </div>
          </div>
          <div className="text-right">
            <div className="font-display text-4xl font-bold text-zinc-950">${PRICE}</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-400">USD</div>
          </div>
        </div>
        <div className="px-6 py-6">
          <ul className="space-y-2 mb-6">
            {[
              "Combine up to 3 analysed products",
              "Opus-grade super-product synthesis",
              "Emergent + Claude Code build plans + PRD export",
              "Build My Own full-spec bundle",
            ].map((f) => (
              <li key={f} className="flex items-start gap-2 text-sm text-zinc-700">
                <CheckCircle size={16} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" />
                {f}
              </li>
            ))}
          </ul>
          <label className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
            Access code
          </label>
          <div className="mt-2 flex border border-zinc-950 bg-white">
            <div className="flex items-center px-4 border-r border-zinc-300 text-zinc-400">
              <LockSimple size={16} />
            </div>
            <Input
              data-testid="studio-code-input"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && code.trim() && m.mutate()}
              placeholder="enter your IQ Studio access code"
              className="sharp flex-1 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 font-mono text-sm h-12 px-4"
            />
            <button
              data-testid="studio-unlock-button"
              onClick={() => (code.trim() ? m.mutate() : toast.error("Enter your access code"))}
              disabled={m.isPending}
              className="bg-emerald-600 text-white px-6 flex items-center gap-2 font-mono text-xs uppercase tracking-widest hover:bg-emerald-700 transition-colors disabled:opacity-60"
            >
              {m.isPending ? "Unlocking…" : "Unlock"}
              <ArrowRight size={16} weight="bold" />
            </button>
          </div>
          <p className="mt-3 font-mono text-[11px] text-zinc-500">
            No account needed — this is an access-code preview. Real billing comes later.
          </p>
        </div>
      </div>
    </div>
  );
}

/* ---------------- Studio home (builder + projects) ---------------- */

const statusChip = {
  queued: "bg-zinc-100 text-zinc-600 border-zinc-300",
  complete: "bg-emerald-50 text-emerald-700 border-emerald-300",
  failed: "bg-red-50 text-red-700 border-red-300",
};

function shortHost(u) {
  try { return new URL(u).host.replace(/^www\./, ""); } catch { return u; }
}

function StudioHome() {
  const navigate = useNavigate();
  const [sources, setSources] = useState([]); // {kind:'analysis'|'url', id?, value?, label}
  const [urlInput, setUrlInput] = useState("");
  const [name, setName] = useState("");
  const [context, setContext] = useState("");

  const { data: projects } = useQuery({ queryKey: ["studio-projects"], queryFn: listStudioProjects });
  const { data: history = [] } = useQuery({ queryKey: ["analyses"], queryFn: listAnalyses });

  const completed = (history || []).filter((h) => h.status === "complete");
  const pickedAnalysisIds = sources.filter((s) => s.kind === "analysis").map((s) => s.id);
  const atMax = sources.length >= 3;

  const addSource = (s) => {
    if (sources.length >= 3) { toast.error("Up to 3 products"); return; }
    if (s.kind === "analysis" && pickedAnalysisIds.includes(s.id)) return;
    setSources((prev) => [...prev, s]);
  };
  const removeSource = (idx) => setSources((prev) => prev.filter((_, i) => i !== idx));

  const addUrl = () => {
    const v = urlInput.trim().replace(/^https?:\/\//i, "");
    if (!v) { toast.error("Enter a URL"); return; }
    addSource({ kind: "url", value: v, label: v });
    setUrlInput("");
  };

  const m = useMutation({
    mutationFn: () =>
      createStudioProject({
        urls: sources.filter((s) => s.kind === "url").map((s) => s.value),
        analysis_ids: pickedAnalysisIds,
        builder_context: context.trim(),
        name: name.trim(),
        code: getCode(),
      }),
    onSuccess: (p) => { toast.success("Project created"); navigate(`/studio/${p.id}`); },
    onError: (err) => toast.error(err?.response?.data?.detail || "Could not create project"),
  });

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-12 py-16" data-testid="studio-home">
      <div className="font-mono text-[11px] uppercase tracking-widest text-emerald-700 mb-4 flex items-center gap-2">
        <Sparkle size={14} weight="fill" /> IQ Studio
      </div>
      <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter text-zinc-950 leading-[0.95]">
        Combine products into<br />a super-product.
      </h1>
      <p className="mt-5 max-w-2xl text-zinc-600 leading-relaxed">
        Pick 2–3 products — from your past teardowns or by pasting a new URL — and Claude Opus
        fuses them into one coherent product with a build plan.
      </p>

      {/* Selected sources */}
      <div className="mt-10 border border-zinc-950 bg-white">
        <div className="px-5 py-3 border-b border-zinc-200 font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center justify-between">
          <span>Products to combine</span>
          <span data-testid="studio-source-count">{sources.length}/3</span>
        </div>
        <div className="p-5">
          {sources.length === 0 ? (
            <div className="font-mono text-xs text-zinc-400 py-2">
              Nothing picked yet — add from history or paste a URL below.
            </div>
          ) : (
            <div className="flex flex-wrap gap-2" data-testid="studio-selected">
              {sources.map((s, i) => (
                <span
                  key={i}
                  data-testid={`studio-source-${i}`}
                  className="inline-flex items-center gap-2 border border-zinc-950 bg-zinc-950 text-white px-3 py-1.5 font-mono text-xs"
                >
                  {s.kind === "analysis" ? <ClockCounterClockwise size={13} /> : <Plus size={12} />}
                  {s.kind === "analysis" ? shortHost(s.label) : s.label}
                  <button onClick={() => removeSource(i)} data-testid={`studio-source-remove-${i}`} className="text-zinc-400 hover:text-white">
                    <X size={13} />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Add new URL */}
          <div className="mt-4 flex border border-zinc-300 bg-white">
            <div className="flex items-center px-4 border-r border-zinc-200 font-mono text-xs text-zinc-400 uppercase">
              https://
            </div>
            <Input
              data-testid="studio-url-input"
              value={urlInput}
              onChange={(e) => setUrlInput(e.target.value.replace(/^https?:\/\//i, ""))}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addUrl())}
              placeholder="paste a new SaaS URL — linear.app"
              disabled={atMax}
              className="sharp flex-1 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 font-mono text-sm h-11 px-4 disabled:opacity-50"
            />
            <button
              onClick={addUrl}
              disabled={atMax}
              data-testid="studio-add-url"
              className="px-4 border-l border-zinc-300 font-mono text-[11px] uppercase tracking-widest text-zinc-700 hover:bg-zinc-50 disabled:opacity-40"
            >
              Add
            </button>
          </div>

          <Input
            data-testid="studio-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Project name (optional)"
            className="sharp mt-3 border border-zinc-300 focus-visible:ring-0 font-mono text-sm h-11"
          />

          <div className="mt-3 border border-zinc-200">
            <div className="px-4 py-2 border-b border-zinc-200 font-mono text-[10px] uppercase tracking-widest text-zinc-500 flex items-center justify-between">
              <span>Optional · what you want the super-product to be</span>
              <span>{context.length}/2000</span>
            </div>
            <Textarea
              data-testid="studio-context-input"
              value={context}
              onChange={(e) => setContext(e.target.value.slice(0, 2000))}
              placeholder="Fuse these into one product that does X better, for Y audience…"
              rows={3}
              className="sharp border-0 focus-visible:ring-0 focus-visible:ring-offset-0 font-mono text-sm resize-none"
            />
          </div>

          <button
            data-testid="studio-create-button"
            onClick={() => (sources.length >= 2 ? m.mutate() : toast.error("Add at least 2 products"))}
            disabled={m.isPending}
            className="w-full mt-4 bg-emerald-600 text-white py-3 flex items-center justify-center gap-2 font-mono text-xs uppercase tracking-widest hover:bg-emerald-700 transition-colors disabled:opacity-60"
          >
            {m.isPending ? "Creating…" : "Analyse & combine"}
            <ArrowRight size={16} weight="bold" />
          </button>
        </div>
      </div>

      {/* Pick from history */}
      {completed.length > 0 && (
        <div className="mt-8" data-testid="studio-history-picker">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3 flex items-center gap-2">
            <ClockCounterClockwise size={13} /> Add from your history
          </div>
          <div className="border border-zinc-200 max-h-80 overflow-y-auto">
            {completed.map((h, idx, arr) => {
              const picked = pickedAnalysisIds.includes(h.id);
              return (
                <button
                  key={h.id}
                  onClick={() => addSource({ kind: "analysis", id: h.id, label: h.target_url })}
                  disabled={picked || atMax}
                  data-testid={`studio-history-${h.id}`}
                  className={`w-full text-left flex items-center gap-3 px-4 py-3 ${idx !== arr.length - 1 ? "border-b border-zinc-200" : ""} ${picked ? "bg-zinc-50 opacity-60" : "hover:bg-zinc-50"} disabled:cursor-not-allowed transition-colors`}
                >
                  <span className="shrink-0 w-2 h-2 bg-emerald-600" />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-xs text-zinc-900 truncate">{shortHost(h.target_url)}</div>
                    {h.vertical && (
                      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{h.vertical}</div>
                    )}
                  </div>
                  {picked ? (
                    <CheckCircle size={16} weight="fill" className="text-emerald-600 shrink-0" />
                  ) : (
                    <Plus size={14} className="text-zinc-400 shrink-0" />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Build My Own bundle (relocated behind the gate) */}
      <a
        href={`${process.env.REACT_APP_BACKEND_URL}/api/build-my-own.zip`}
        data-testid="studio-build-my-own"
        className="mt-8 inline-flex items-center gap-2 border border-zinc-300 text-zinc-800 px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-zinc-50 transition-colors"
      >
        <Package size={14} weight="fill" /> Build My Own — download full-spec bundle
      </a>

      {/* Past projects */}
      {projects?.items?.length > 0 && (
        <div className="mt-14">
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">
            Your super-products
          </div>
          <div className="border border-zinc-200" data-testid="studio-project-list">
            {projects.items.map((p, idx, arr) => (
              <Link
                key={p.id}
                to={`/studio/${p.id}`}
                data-testid={`studio-project-${p.id}`}
                className={`flex items-center gap-3 px-5 py-4 hover:bg-zinc-50 transition-colors ${
                  idx !== arr.length - 1 ? "border-b border-zinc-200" : ""
                }`}
              >
                <Sparkle size={16} weight="fill" className="text-emerald-600 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-medium text-zinc-950 truncate">
                    {p.product_name || p.name}
                  </div>
                  <div className="font-mono text-[11px] text-zinc-500">
                    {p.count} products · {p.status}
                  </div>
                </div>
                <ArrowRight size={16} className="text-zinc-400" />
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- Export row ---------------- */

function ExportButton({ id, kind, label, testid }) {
  const copy = async () => {
    try {
      const text = await fetchStudioExport(id, kind);
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <div className="flex border border-zinc-300" data-testid={`${testid}-row`}>
      <div className="flex-1 px-4 py-3 font-mono text-xs uppercase tracking-widest text-zinc-800">
        {label}
      </div>
      <button
        onClick={copy}
        data-testid={`${testid}-copy`}
        className="px-4 border-l border-zinc-300 text-zinc-600 hover:bg-zinc-50 flex items-center gap-1.5 font-mono text-[11px] uppercase"
        title="Copy to clipboard"
      >
        <Copy size={14} /> Copy
      </button>
      <a
        href={studioExportUrl(id, kind)}
        download={`${kind}.md`}
        data-testid={`${testid}-download`}
        className="px-4 border-l border-zinc-300 text-zinc-600 hover:bg-zinc-50 flex items-center gap-1.5 font-mono text-[11px] uppercase"
        title="Download Markdown"
      >
        <DownloadSimple size={14} /> MD
      </a>
    </div>
  );
}

/* ---------------- Plan view ---------------- */

function OnboardingBlueprints({ project }) {
  const qc = useQueryClient();
  const products = project.products || [];
  const ready = project.onboarding_ready || 0;
  const pending = project.onboarding_pending || 0;
  const missing = products.length - ready - pending;

  const queue = useMutation({
    mutationFn: () => studioQueueOnboarding(project.id),
    onSuccess: (d) => {
      toast.success(d.queued ? `Reconstructing onboarding for ${d.queued} product(s)…` : "Nothing left to reconstruct");
      qc.invalidateQueries({ queryKey: ["studio-project", project.id] });
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Could not queue onboarding"),
  });

  return (
    <div className="mt-4 border border-zinc-200 bg-zinc-50 p-4" data-testid="studio-onboarding-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
            Onboarding blueprints in the plan
          </div>
          <div className="text-sm text-zinc-800" data-testid="studio-onboarding-count">
            {ready} of {products.length} source products have a reconstructed activation journey
            {pending > 0 ? ` · ${pending} in progress` : ""}.
            {ready > 0
              ? " These are folded into the Emergent and Claude Code plans."
              : " Reconstruct them to get an activation plan inside the Emergent and Claude Code plans."}
          </div>
        </div>
        {(missing > 0 || pending > 0) && (
          <button
            onClick={() => queue.mutate()}
            disabled={queue.isPending || pending > 0}
            data-testid="studio-queue-onboarding"
            className="border border-zinc-950 bg-white px-3 py-2 font-mono text-[10px] uppercase tracking-widest hover:bg-zinc-950 hover:text-white transition-colors disabled:opacity-60 flex items-center gap-1.5"
          >
            {pending > 0 ? <Spinner size={11} className="animate-spin" /> : <Path size={11} />}
            {pending > 0 ? "Reconstructing…" : "Add onboarding blueprints"}
          </button>
        )}
      </div>
    </div>
  );
}

function PlanView({ project }) {
  const p = project.super_product;
  return (
    <div data-testid="studio-plan">
      <div className="border border-zinc-950 bg-zinc-950 text-white p-8">
        <div className="font-mono text-[10px] uppercase tracking-widest text-emerald-400 mb-3 flex items-center gap-2">
          <Sparkle size={13} weight="fill" /> Super-product · synthesised by {p.model || "Claude"}
        </div>
        <h2 className="font-display text-4xl font-bold tracking-tight">{p.product_name}</h2>
        {p.tagline && <p className="mt-2 text-lg text-zinc-300">{p.tagline}</p>}
        {p.elevator_pitch && <p className="mt-5 text-zinc-200 leading-relaxed max-w-3xl">{p.elevator_pitch}</p>}
        <div className="mt-6 grid sm:grid-cols-2 gap-4">
          {p.target_market && (
            <div className="border-t border-zinc-700 pt-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Target market</div>
              <div className="text-sm text-zinc-200 mt-1">{p.target_market}</div>
            </div>
          )}
          {p.positioning && (
            <div className="border-t border-zinc-700 pt-3">
              <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Positioning</div>
              <div className="text-sm text-zinc-200 mt-1">{p.positioning}</div>
            </div>
          )}
        </div>
        {p.synthesis_rationale && (
          <div className="mt-4 border-t border-zinc-700 pt-3">
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Why this combination</div>
            <div className="text-sm text-zinc-200 mt-1">{p.synthesis_rationale}</div>
          </div>
        )}
      </div>

      {/* Exports */}
      <div className="mt-8">
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">
          Hand it to a builder
        </div>
        <div className="grid sm:grid-cols-3 gap-3">
          <ExportButton id={project.id} kind="emergent" label="Emergent plan" testid="export-emergent" />
          <ExportButton id={project.id} kind="claude-code" label="Claude Code plan" testid="export-claude" />
          <ExportButton id={project.id} kind="prd" label="PRD" testid="export-prd" />
        </div>
        <a
          href="https://app.emergent.sh"
          target="_blank"
          rel="noreferrer"
          data-testid="open-emergent-link"
          className="mt-3 inline-flex items-center gap-2 bg-emerald-600 text-white px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-emerald-700 transition-colors"
        >
          Open Emergent <ArrowRight size={14} weight="bold" />
        </a>
        <OnboardingBlueprints project={project} />
      </div>

      {/* Combined features */}
      <div className="mt-10">
        <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">
          Combined feature set · {p.combined_features.length}
        </div>
        <div className="grid sm:grid-cols-2 gap-px bg-zinc-200 border border-zinc-200">
          {p.combined_features.map((f, i) => (
            <div key={i} className="bg-white p-4" data-testid={`studio-feature-${i}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="font-display font-semibold text-zinc-950">{f.name}</div>
                {f.is_novel && (
                  <span className="shrink-0 inline-flex items-center gap-1 bg-amber-100 text-amber-700 border border-amber-300 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest">
                    <Star size={10} weight="fill" /> Novel
                  </span>
                )}
              </div>
              {f.description && <div className="text-sm text-zinc-600 mt-1 leading-relaxed">{f.description}</div>}
              {f.source_products?.length > 0 && (
                <div className="mt-2 font-mono text-[10px] text-zinc-400 truncate">
                  from: {f.source_products.join(", ")}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Differentiation */}
      {p.differentiation?.length > 0 && (
        <Section title="Differentiation">
          <ul className="space-y-2">
            {p.differentiation.map((d, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-zinc-700">
                <CheckCircle size={16} weight="fill" className="text-emerald-600 mt-0.5 shrink-0" />
                {d}
              </li>
            ))}
          </ul>
        </Section>
      )}

      {/* Roadmap */}
      {p.roadmap?.length > 0 && (
        <Section title="Build roadmap">
          <div className="border border-zinc-200">
            {p.roadmap.map((r, i, arr) => (
              <div key={i} className={`p-5 ${i !== arr.length - 1 ? "border-b border-zinc-200" : ""}`}>
                <div className="font-display font-semibold text-zinc-950">{r.phase}</div>
                {r.goal && <div className="text-sm text-zinc-600 mt-0.5">{r.goal}</div>}
                <ul className="mt-2 space-y-1">
                  {r.items.map((it, j) => (
                    <li key={j} className="font-mono text-xs text-zinc-600 flex gap-2">
                      <span className="text-emerald-600">›</span> {it}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Tech + monetization + risks */}
      <div className="mt-10 grid md:grid-cols-2 gap-8">
        {p.tech_stack?.length > 0 && (
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Recommended stack</div>
            <div className="flex flex-wrap gap-2">
              {p.tech_stack.map((t, i) => (
                <span key={i} className="border border-zinc-300 px-2 py-1 font-mono text-[11px] text-zinc-700">
                  {t}
                </span>
              ))}
            </div>
          </div>
        )}
        {p.risks?.length > 0 && (
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">Risks & hard parts</div>
            <ul className="space-y-2">
              {p.risks.map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-zinc-700">
                  <Warning size={15} weight="fill" className="text-amber-600 mt-0.5 shrink-0" />
                  {r}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
      {p.monetization && (
        <Section title="Monetization">
          <p className="text-sm text-zinc-700 leading-relaxed">{p.monetization}</p>
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-10">
      <div className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mb-3">{title}</div>
      {children}
    </div>
  );
}

/* ---------------- Project view ---------------- */

function ProjectView({ id }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: project, isLoading } = useQuery({
    queryKey: ["studio-project", id],
    queryFn: () => getStudioProject(id),
    refetchInterval: (q) => {
      const d = q.state.data;
      const s = d?.status;
      if ((d?.onboarding_pending || 0) > 0) return 4000;
      return s === "complete" || s === "failed" ? false : 4000;
    },
  });

  const synth = useMutation({
    mutationFn: () => synthesizeStudio(id, getCode()),
    onSuccess: () => {
      toast.success("Synthesising super-product…");
      qc.invalidateQueries({ queryKey: ["studio-project", id] });
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Could not start synthesis"),
  });

  const del = useMutation({
    mutationFn: () => deleteStudioProject(id),
    onSuccess: () => {
      toast.success("Project deleted");
      navigate("/studio");
    },
  });

  if (isLoading || !project) {
    return <div className="max-w-5xl mx-auto px-6 py-20 font-mono text-sm text-zinc-500">Loading…</div>;
  }

  const products = project.products || [];
  const allComplete = products.length > 0 && products.every((p) => p.status === "complete");

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-12 py-14" data-testid="studio-project-view">
      <div className="flex items-center justify-between">
        <Link to="/studio" className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 hover:text-zinc-900">
          ← IQ Studio
        </Link>
        <button
          onClick={() => del.mutate()}
          data-testid="studio-delete-button"
          className="font-mono text-[11px] uppercase tracking-widest text-zinc-400 hover:text-red-600"
        >
          Delete
        </button>
      </div>

      <h1 className="mt-5 font-display text-4xl font-bold tracking-tighter text-zinc-950">
        {project.super_product?.product_name || project.name}
      </h1>

      {/* Input products */}
      <div className="mt-8 grid sm:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200" data-testid="studio-inputs">
        {products.map((pr) => (
          <div key={pr.id} className="bg-white p-4">
            <div className="flex items-center gap-2">
              {pr.status === "complete" ? (
                <CheckCircle size={15} weight="fill" className="text-emerald-600" />
              ) : pr.status === "failed" ? (
                <Warning size={15} weight="fill" className="text-red-600" />
              ) : (
                <CircleNotch size={15} className="text-amber-500 animate-spin" />
              )}
              <span className="font-mono text-[11px] text-zinc-900 truncate">
                {pr.target_url.replace(/^https?:\/\//, "").replace(/\/$/, "")}
              </span>
            </div>
            <div className={`mt-2 inline-block border px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-widest ${statusChip[pr.status] || "bg-amber-50 text-amber-700 border-amber-300"}`}>
              {pr.status}
            </div>
            {pr.vertical && <div className="mt-2 text-[11px] text-zinc-500 font-mono uppercase">{pr.vertical}</div>}
          </div>
        ))}
      </div>

      {/* Synthesis control / states */}
      {project.status !== "complete" && (
        <div className="mt-8 border border-zinc-200 bg-zinc-50 p-6" data-testid="studio-synth-panel">
          {project.status === "synthesizing" ? (
            <div className="flex items-center gap-3 text-zinc-700">
              <CircleNotch size={18} className="animate-spin text-emerald-600" />
              <span className="font-mono text-sm">Fusing the products into one super-product…</span>
            </div>
          ) : project.status === "failed" ? (
            <div>
              <div className="flex items-center gap-2 text-red-700 font-mono text-sm">
                <Warning size={16} weight="fill" /> {project.error || "Synthesis failed"}
              </div>
              <button
                onClick={() => synth.mutate()}
                data-testid="studio-retry-button"
                className="mt-3 bg-zinc-950 text-white px-4 py-2 font-mono text-xs uppercase tracking-widest hover:bg-zinc-800"
              >
                Retry synthesis
              </button>
            </div>
          ) : allComplete ? (
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="font-mono text-sm text-zinc-700">
                All products analysed. Ready to synthesise the super-product.
              </div>
              <button
                onClick={() => synth.mutate()}
                disabled={synth.isPending}
                data-testid="studio-synthesize-button"
                className="bg-emerald-600 text-white px-5 py-2.5 flex items-center gap-2 font-mono text-xs uppercase tracking-widest hover:bg-emerald-700 transition-colors disabled:opacity-60"
              >
                <Sparkle size={15} weight="fill" /> Synthesise super-product
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3 text-zinc-700">
              <CircleNotch size={18} className="animate-spin text-amber-500" />
              <span className="font-mono text-sm">
                Analysing products… this runs one at a time and can take a few minutes.
              </span>
            </div>
          )}
        </div>
      )}

      {project.status === "complete" && project.super_product && (
        <div className="mt-10">
          <PlanView project={project} />
        </div>
      )}
    </div>
  );
}

/* ---------------- Page ---------------- */

export default function Studio() {
  const { id } = useParams();
  const [unlocked, setUnlocked] = useState(localStorage.getItem(UNLOCK_KEY) === "1");

  if (!unlocked) {
    return (
      <div className="flex-1 min-h-screen bg-white">
        <LockGate onUnlock={() => setUnlocked(true)} />
      </div>
    );
  }
  return (
    <div className="flex-1 min-h-screen bg-white">
      {id ? <ProjectView id={id} /> : <StudioHome />}
    </div>
  );
}
